const DEFAULT_DESCRIPTION =
  'Guess the word using zoomed-in images, riddles, and clues in quick puzzle rounds.';

const INDEX_ROBOTS = 'index, follow';
const NOINDEX_ROBOTS = 'noindex, nofollow';

export const BASE_SITE_URL = 'https://zoomed-in.com';

export const normalizePathname = (pathname) => {
  if (typeof pathname !== 'string' || pathname.length === 0) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

export const EXACT_ROUTE_SEO = Object.freeze({
  '/': Object.freeze({
    title: 'Zoomed In - Visual Word Puzzle Game',
    description:
      'Play Daily, Unlimited, Generations, Earth, and Riddled-In puzzle modes in Zoomed In.',
    robots: INDEX_ROBOTS,
  }),
  '/daily': Object.freeze({
    title: 'Zoomed In Daily - One Shared Puzzle',
    description:
      'Play the daily shared Zoomed In puzzle and compare your progress with other players.',
    robots: INDEX_ROBOTS,
  }),
  '/unlimited': Object.freeze({
    title: 'Zoomed In Unlimited - Endless Puzzle Rounds',
    description:
      'Keep solving zoomed image rounds with unlimited replay and clue-based progression.',
    robots: INDEX_ROBOTS,
  }),
  '/generations': Object.freeze({
    title: 'Zoomed In Generations - AI Image Puzzle Mode',
    description:
      'Solve AI-generated image puzzle rounds with clue and coin mechanics in Generations mode.',
    robots: INDEX_ROBOTS,
  }),
  '/riddled-in': Object.freeze({
    title: 'Riddled-In - Riddle Puzzle Mode | Zoomed In',
    description:
      'Solve riddle-first puzzle rounds where clue interpretation matters as much as the image.',
    robots: INDEX_ROBOTS,
  }),
  '/earth': Object.freeze({
    title: 'Zoomed In Earth - Street View Guessing',
    description:
      'Play map-based Street View guessing rounds and test your location intuition in Earth mode.',
    robots: INDEX_ROBOTS,
  }),
  '/leaderboard': Object.freeze({
    title: 'Leaderboard - Zoomed In',
    description:
      'Track top puzzle performance across game modes and compare your results over time.',
    robots: NOINDEX_ROBOTS,
  }),
  '/coins': Object.freeze({
    title: 'Coin Shop - Zoomed In',
    description:
      'Manage your coin balance and unlock optional in-app puzzle boosts and paid features.',
    robots: NOINDEX_ROBOTS,
  }),
  '/about': Object.freeze({
    title: 'About Zoomed In',
    description:
      'Learn what Zoomed In is, how the puzzle platform works, and who builds the project.',
    robots: INDEX_ROBOTS,
  }),
  '/contact': Object.freeze({
    title: 'Contact Zoomed In',
    description:
      'Get support, report issues, and share product feedback with the Zoomed In team.',
    robots: INDEX_ROBOTS,
  }),
  '/privacy': Object.freeze({
    title: 'Privacy Policy - Zoomed In',
    description:
      'Read how Zoomed In collects, uses, and protects data across gameplay and account features.',
    robots: INDEX_ROBOTS,
  }),
  '/terms': Object.freeze({
    title: 'Terms and Conditions - Zoomed In',
    description:
      'Review account, payment, and platform rules for using Zoomed In and in-app features.',
    robots: INDEX_ROBOTS,
  }),
  '/play': Object.freeze({
    title: 'Zoomed In Classic Mode',
    description: DEFAULT_DESCRIPTION,
    robots: INDEX_ROBOTS,
  }),
  '/hardcore': Object.freeze({
    title: 'Zoomed In Hardcore Mode',
    description: DEFAULT_DESCRIPTION,
    robots: INDEX_ROBOTS,
  }),
  '/custom/create': Object.freeze({
    title: 'Create Custom Game - Zoomed In',
    description:
      'Create and manage your own custom Zoomed In puzzle rounds with image and clue controls.',
    robots: NOINDEX_ROBOTS,
  }),
  '/admin/daily': Object.freeze({
    title: 'Daily Admin - Zoomed In',
    description: 'Admin-only daily puzzle management page.',
    robots: NOINDEX_ROBOTS,
  }),
});

export const PREFIX_ROUTE_SEO = Object.freeze([
  Object.freeze({
    prefix: '/auth',
    seo: Object.freeze({
      title: 'Account - Zoomed In',
      description: 'Sign in, reset password, and manage your Zoomed In account settings.',
      robots: NOINDEX_ROBOTS,
    }),
  }),
  Object.freeze({
    prefix: '/custom/',
    seo: Object.freeze({
      title: 'Custom Game - Zoomed In',
      description:
        'Play a shared Zoomed In custom puzzle. Shared game routes are not intended for search indexing.',
      robots: NOINDEX_ROBOTS,
    }),
  }),
]);

export const FALLBACK_ROUTE_SEO = Object.freeze({
  title: 'Page Not Found - Zoomed In',
  description: 'The requested page could not be found.',
  robots: NOINDEX_ROBOTS,
  canonicalPath: null,
});

export const getSeoForPathname = (pathname) => {
  const normalized = normalizePathname(pathname);
  const exactSeo = EXACT_ROUTE_SEO[normalized];
  if (exactSeo) {
    return {
      ...exactSeo,
      canonicalPath: normalized,
    };
  }

  const prefixRule = PREFIX_ROUTE_SEO.find((rule) =>
    normalized.startsWith(rule.prefix)
  );
  if (prefixRule) {
    return {
      ...prefixRule.seo,
      canonicalPath: normalized,
    };
  }

  return {
    ...FALLBACK_ROUTE_SEO,
    canonicalPath: normalized,
  };
};
